import { useEffect, useMemo, useRef, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import { v4 as uuid } from 'uuid';
import {
  DirectoryOperationsService,
  parseDirectoryError,
} from '../Directory/operations/DirectoryOperations.service';
import type {
  tCapturedSelection,
  tDirectoryApiError,
} from '../Directory/operations/DirectoryOperations.types';
import { useDirectoryOperationsWithRecovery } from '../Directory/operations/useDirectoryOperationsWithRecovery.hook';
import type { tFunnelLead } from './Pipe.types';
import type { tMatchingQuery } from '../Directory/Directory.matching';

type tOptions = {
  enabled: boolean;
  workspaceId?: string;
  actorId?: string;
  funnelId?: string;
  stageId: string | null;
  viewKey: string;
  matchingQuery: tMatchingQuery | null;
  sourceReady: boolean;
  loadedLeadIds: string[];
  stageLeadCount?: number;
  defaultTitle: (kind: string) => string;
  undoTitle: (title: string) => string;
  onAccepted: () => void;
  onFinished: () => void;
};

type tStageSelectionError =
  tDirectoryApiError | { reason: 'source_population_changed' };

/** Only selection state belongs to the board; durable execution/recovery is Directory's. */
export function useFunnelBulkOperations(options: tOptions) {
  const context = JSON.stringify([
    options.workspaceId,
    options.actorId,
    options.funnelId,
    options.stageId,
    options.viewKey,
    options.enabled,
    options.matchingQuery,
  ]);
  const scope = useRef({ context, epoch: 0 });
  if (scope.current.context !== context)
    scope.current = { context, epoch: scope.current.epoch + 1 };
  const epoch = scope.current.epoch;
  const [snapshot, setSnapshot] = useState<{
    epoch: number;
    selection: tCapturedSelection;
  } | null>(null);
  const selection = snapshot?.epoch === epoch ? snapshot.selection : null;
  const [members, setMembers] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<tStageSelectionError | null>(null);
  const membershipPending = Boolean(
    selection && !error && options.loadedLeadIds.some((id) => !(id in members)),
  );
  const inFlight = useRef(false);
  const captureKey = useRef(uuid());
  const verifiedSelection = useRef<string | null>(null);
  const anchor = useRef<string | null>(null);
  const operationEpoch = useRef<number | null>(null);

  const operations = useDirectoryOperationsWithRecovery({
    workspaceId: options.workspaceId,
    actorId: options.actorId,
    // A rollout rollback stops admission, but accepted operations still recover.
    enabled: true,
    defaultTitle: options.defaultTitle,
    undoTitle: options.undoTitle,
    onOperationFinished: options.onFinished,
    onOperationAccepted: () => {
      if (operationEpoch.current !== scope.current.epoch) return;
      clear();
      options.onAccepted();
    },
    openLegacyDialog: () => {},
    allowLegacyFallback: false,
  });

  function current(expected: number) {
    return scope.current.epoch === expected;
  }

  function clear() {
    scope.current.epoch += 1;
    setSnapshot(null);
    setMembers({});
    setError(null);
    captureKey.current = uuid();
    verifiedSelection.current = null;
    anchor.current = null;
    inFlight.current = false;
    setBusy(false);
  }

  useEffect(() => {
    clear();
    operations.closeOperationDialog();
    // Accepted operations intentionally survive source-stage/filter/funnel changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- BUG-1115 U8: preserve the existing trigger array (KTD4).
  }, [context]);

  useEffect(
    () => () => {
      scope.current.epoch += 1;
    },
    [],
  );

  async function readMembers(resource: tCapturedSelection, ids: string[]) {
    const workspaceId = options.workspaceId;
    if (!workspaceId) throw new Error('Selection workspace is required');
    const result: Record<string, boolean> = {};
    // Membership accepts at most 500 loaded IDs. Never send the entire stage population.
    const unique = [...new Set(ids)];
    for (let start = 0; start < unique.length; start += 500) {
      const batch = unique.slice(start, start + 500);
      const response = await DirectoryOperationsService.membership(
        workspaceId,
        {
          selectionId: resource.id,
          leadIds: batch,
        },
      );
      if (
        Object.keys(response.members).length !== batch.length ||
        batch.some((id) => typeof response.members[id] !== 'boolean')
      )
        throw new Error('Invalid membership response');
      Object.assign(result, response.members);
    }
    return result;
  }

  async function selectStage() {
    if (
      !options.enabled ||
      !options.matchingQuery ||
      (!selection && !options.sourceReady) ||
      !options.workspaceId ||
      !options.funnelId ||
      !options.stageId ||
      inFlight.current
    )
      return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    const expected = scope.current.epoch;
    try {
      const resource = selection
        ? await DirectoryOperationsService.readSelection(
            options.workspaceId,
            selection.id,
          )
        : await DirectoryOperationsService.captureMatching(
            options.workspaceId,
            {
              query: options.matchingQuery,
              idempotencyKey: captureKey.current,
            },
          );
      if (!current(expected)) return;
      setSnapshot({ epoch: expected, selection: resource });
      const loaded = await readMembers(resource, options.loadedLeadIds);
      if (!current(expected)) return;
      if (
        verifiedSelection.current !== resource.id &&
        (resource.member_count !== options.stageLeadCount ||
          options.loadedLeadIds.some((id) => loaded[id] !== true))
      ) {
        setError({ reason: 'source_population_changed' });
        return;
      }
      verifiedSelection.current = resource.id;
      setMembers(loaded);
    } catch (err) {
      if (current(expected)) setError(parseDirectoryError(err));
    } finally {
      if (current(expected)) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  }

  // Newly loaded cards are checked against the original snapshot, never assumed selected.
  const loadedKey = options.loadedLeadIds.join(',');
  useEffect(() => {
    if (!selection || !options.workspaceId || inFlight.current || busy || error)
      return undefined;
    const unknown = options.loadedLeadIds.filter((id) => !(id in members));
    if (!unknown.length) return undefined;
    let cancelled = false;
    const expected = scope.current.epoch;
    readMembers(selection, unknown)
      .then((loaded) => {
        if (!cancelled && current(expected))
          setMembers((prev) => ({ ...prev, ...loaded }));
      })
      .catch((err: unknown) => {
        if (!cancelled && current(expected)) setError(parseDirectoryError(err));
      });
    return () => {
      cancelled = true;
    };
    // Refresh after pagination or an exclusion revision, not on every board render.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- BUG-1115 U8: preserve the existing trigger array (KTD4).
  }, [selection?.id, selection?.revision, loadedKey, context, busy, error]);

  async function toggle(input: {
    leadId: string;
    range: boolean;
    orderedLeadIds?: string[];
  }) {
    if (
      !selection ||
      !options.workspaceId ||
      busy ||
      membershipPending ||
      inFlight.current ||
      error ||
      !(input.leadId in members)
    )
      return;
    const order = input.orderedLeadIds ?? options.loadedLeadIds;
    const from = anchor.current ? order.indexOf(anchor.current) : -1;
    const to = order.indexOf(input.leadId);
    const ids =
      input.range && from >= 0 && to >= 0
        ? order.slice(Math.min(from, to), Math.max(from, to) + 1)
        : [input.leadId];
    const exclude = input.range ? [] : ids.filter((id) => members[id] === true);
    const reinclude = ids.filter((id) => members[id] === false);
    if (!input.range) anchor.current = input.leadId;
    if (!exclude.length && !reinclude.length) return;
    inFlight.current = true;
    setBusy(true);
    const expected = scope.current.epoch;
    try {
      const updated = await DirectoryOperationsService.patchSelection(
        options.workspaceId,
        {
          selectionId: selection.id,
          expectedRevision: selection.revision,
          exclude,
          reinclude,
        },
      );
      if (!current(expected)) return;
      setSnapshot({ epoch: expected, selection: updated });
      const loaded = await readMembers(updated, options.loadedLeadIds);
      if (current(expected)) setMembers(loaded);
    } catch (err) {
      if (current(expected)) setError(parseDirectoryError(err));
    } finally {
      if (current(expected)) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  }

  function openOperation(request: {
    kind: 'move' | 'assign' | 'archive' | 'tag';
    leads: tFunnelLead[];
  }) {
    if (
      !options.enabled ||
      busy ||
      membershipPending ||
      error ||
      inFlight.current
    )
      return;
    operationEpoch.current = scope.current.epoch;
    if (selection) {
      if (selection.selected_count)
        operations.openOperationDialog({ kind: request.kind, selection });
    } else if (request.leads.length) {
      operations.openOperationDialog({
        kind: request.kind,
        leads: request.leads.map((lead) => ({ id: lead.id, active: true })),
      });
    }
  }

  const stableSelectStage = useMemoizedFn(selectStage);
  const stableToggle = useMemoizedFn(toggle);
  const stableClear = useMemoizedFn(clear);
  const stableOpenOperation = useMemoizedFn(openOperation);
  // Busy means server work in flight. A column whose loaded cards do not match
  // the board view yet cannot be captured (`waitingForSource`), but cards
  // picked one by one never go through the capture, so it must not disable
  // their actions (BUG-1165).
  const isBusy = busy || membershipPending;
  const waitingForSource = Boolean(
    options.enabled && options.matchingQuery && !selection && !options.sourceReady,
  );
  const canCapture = options.enabled && options.matchingQuery !== null;
  return useMemo(() => ({
    selection,
    members,
    busy: isBusy,
    waitingForSource,
    error,
    enabled: options.enabled,
    canCapture,
    selectStage: stableSelectStage,
    toggle: stableToggle,
    clear: stableClear,
    openOperation: stableOpenOperation,
    operations,
  }), [
    selection, members, isBusy, waitingForSource, error, options.enabled, canCapture,
    stableSelectStage, stableToggle, stableClear, stableOpenOperation, operations,
  ]);
}

export type tFunnelBulkOperations = ReturnType<typeof useFunnelBulkOperations>;
